// Waitlist form (/aulas) — Zod validation + EmailJS send.
// Env vars: VITE_EMAILJS_SERVICE_ID, VITE_EMAILJS_WAITLIST_TEMPLATE_ID
//           (fallback no VITE_EMAILJS_TEMPLATE_ID se waitlist template não setado),
//           VITE_EMAILJS_PUBLIC_KEY.
// Em dev/preview sem env vars, mostra modo demo (sem enviar — log + status info).

import emailjs from "@emailjs/browser";
import { z } from "zod";

import { getLanguage, onLanguageChange, t } from "../lib/i18n";

const INTEREST_VALUES = ["work", "company", "partner", "notify"] as const;
type Interest = (typeof INTEREST_VALUES)[number];

const FIELDS = ["name", "email", "whatsapp", "interest"] as const;
type FieldName = (typeof FIELDS)[number];

function getSchema() {
  // `t()` reavaliado em cada validate → mensagens refletem idioma corrente.
  return z.object({
    name: z
      .string()
      .trim()
      .min(2, { message: t("waitlist.error_name_min") })
      .max(120, { message: t("waitlist.error_name_max") }),
    email: z
      .string()
      .trim()
      .email({ message: t("waitlist.error_email_invalid") })
      .max(200, { message: t("waitlist.error_email_max") }),
    whatsapp: z
      .string()
      .trim()
      .optional()
      .refine((v) => !v || /^[\d\s()+\-.]{8,20}$/.test(v), {
        message: t("waitlist.error_whatsapp_invalid"),
      }),
    interest: z.enum(INTEREST_VALUES, {
      message: t("waitlist.error_interest_required"),
    }),
  });
}

type FormState = "idle" | "loading" | "success" | "error";

export function initWaitlistForm(): void {
  const formEl = document.getElementById("waitlistForm") as HTMLFormElement | null;
  if (!formEl) return;
  const form: HTMLFormElement = formEl;

  const submitBtn = form.querySelector<HTMLButtonElement>("#waitlistSubmit");
  const submitLabel = form.querySelector<HTMLElement>("[data-form-label]");
  const statusEl = form.querySelector<HTMLElement>("[data-form-status]");

  const nameInput = form.querySelector<HTMLInputElement>("#wf-name");
  const emailInput = form.querySelector<HTMLInputElement>("#wf-email");
  const whatsappInput = form.querySelector<HTMLInputElement>("#wf-whatsapp");
  const interestInputs = form.querySelectorAll<HTMLInputElement>('input[name="interest"]');

  if (!nameInput || !emailInput || !whatsappInput) return;

  const errorEls: Record<FieldName, HTMLElement | null> = {
    name: form.querySelector('[data-error-for="name"]'),
    email: form.querySelector('[data-error-for="email"]'),
    whatsapp: form.querySelector('[data-error-for="whatsapp"]'),
    interest: form.querySelector('[data-error-for="interest"]'),
  };

  // EmailJS env config — exige prefixo VITE_ pra chegar no bundle do cliente.
  const serviceId = import.meta.env.VITE_EMAILJS_SERVICE_ID as string | undefined;
  const waitlistTemplateId = import.meta.env.VITE_EMAILJS_WAITLIST_TEMPLATE_ID as string | undefined;
  const mainTemplateId = import.meta.env.VITE_EMAILJS_TEMPLATE_ID as string | undefined;
  const publicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY as string | undefined;
  // Fallback: usa template principal se o dedicado não foi setado.
  const templateId = waitlistTemplateId || mainTemplateId;
  const demoMode = !serviceId || !templateId || !publicKey;

  if (!demoMode && publicKey) {
    emailjs.init({ publicKey });
  }

  let state: FormState = "idle";
  let lastStatusKey = "";
  let lastStatusKind: "" | "success" | "error" | "info" = "";

  function submitLabelKey(s: FormState): string {
    if (s === "loading") return "waitlist.submit_loading";
    if (s === "success") return "waitlist.submit_success";
    return "waitlist.submit_idle";
  }

  function setState(next: FormState): void {
    state = next;
    if (submitBtn) submitBtn.disabled = next === "loading";
    if (submitLabel) submitLabel.textContent = t(submitLabelKey(next));
  }

  function setStatus(kind: "" | "success" | "error" | "info", msgKey: string): void {
    lastStatusKind = kind;
    lastStatusKey = msgKey;
    if (!statusEl) return;
    statusEl.classList.remove("success", "error", "info");
    if (!kind) {
      statusEl.textContent = "";
      return;
    }
    statusEl.classList.add(kind);
    statusEl.textContent = msgKey ? t(msgKey) : "";
  }

  function clearErrors(): void {
    for (const name of FIELDS) {
      const errEl = errorEls[name];
      if (errEl) errEl.textContent = "";
    }
    nameInput!.removeAttribute("aria-invalid");
    emailInput!.removeAttribute("aria-invalid");
    whatsappInput!.removeAttribute("aria-invalid");
  }

  function showErrors(issues: z.ZodIssue[]): void {
    clearErrors();
    for (const issue of issues) {
      const name = issue.path[0] as FieldName | undefined;
      if (!name) continue;
      const errEl = errorEls[name];
      if (errEl) errEl.textContent = issue.message;
      if (name === "name") nameInput!.setAttribute("aria-invalid", "true");
      else if (name === "email") emailInput!.setAttribute("aria-invalid", "true");
      else if (name === "whatsapp") whatsappInput!.setAttribute("aria-invalid", "true");
    }
  }

  function readInterest(): Interest | undefined {
    for (const input of Array.from(interestInputs)) {
      if (input.checked) {
        const v = input.value;
        if ((INTEREST_VALUES as readonly string[]).includes(v)) {
          return v as Interest;
        }
      }
    }
    return undefined;
  }

  function readValues(): {
    name: string;
    email: string;
    whatsapp: string;
    interest: Interest | undefined;
  } {
    return {
      name: nameInput!.value,
      email: emailInput!.value,
      whatsapp: whatsappInput!.value,
      interest: readInterest(),
    };
  }

  function interestLabel(interest: Interest): string {
    return t(`waitlist.interest_${interest}`);
  }

  // Monta uma `message` sintética pro template principal de EmailJS (que tem
  // `{{message}}`). Assim o template de contato consegue renderizar o waitlist
  // sem precisar de template dedicado. Labels em PT — time lê em PT, e o
  // `interest_label` reflete o idioma que o usuário escolheu.
  function buildMessage(interest: Interest, whatsapp: string, language: string): string {
    const lines = [
      "[Lista de espera /aulas — QualiApps]",
      "",
      `Interesse principal: ${interestLabel(interest)}`,
    ];
    if (whatsapp.trim()) {
      lines.push(`WhatsApp: ${whatsapp.trim()}`);
    }
    lines.push(`Origem: waitlist_aulas`);
    lines.push(`Idioma do formulário: ${language}`);
    return lines.join("\n");
  }

  async function onSubmit(ev: SubmitEvent): Promise<void> {
    ev.preventDefault();
    if (state === "loading") return;

    setStatus("", "");
    const values = readValues();
    const parsed = getSchema().safeParse(values);
    if (!parsed.success) {
      showErrors(parsed.error.issues);
      setStatus("error", "waitlist.status_validation");
      return;
    }
    clearErrors();

    const lang = getLanguage();
    const whatsappValue = parsed.data.whatsapp ?? "";
    const emailPayload = {
      from_name: parsed.data.name,
      reply_to: parsed.data.email,
      whatsapp: whatsappValue,
      interest: parsed.data.interest,
      interest_label: interestLabel(parsed.data.interest),
      source: "waitlist_aulas",
      language: lang,
      // `message` sintética: garante que o template principal de contato
      // (com {{message}}) renderize o waitlist completo. Templates dedicados
      // que ignorem `message` em favor dos campos individuais também funcionam.
      message: buildMessage(parsed.data.interest, whatsappValue, lang),
    };

    if (demoMode) {
      // eslint-disable-next-line no-console
      console.info(
        "[waitlist-form] EmailJS env vars não configuradas — modo demo. Payload:",
        emailPayload,
      );
      setState("loading");
      window.setTimeout(() => {
        setState("success");
        setStatus("info", "waitlist.status_demo");
      }, 600);
      return;
    }

    setState("loading");
    try {
      await emailjs.send(serviceId!, templateId!, emailPayload);
      setState("success");
      setStatus("success", "waitlist.status_success");
      form.reset();
      window.setTimeout(() => setState("idle"), 5000);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[waitlist-form] EmailJS error", err);
      setState("error");
      setStatus("error", "waitlist.status_error");
      window.setTimeout(() => setState("idle"), 4000);
    }
  }

  form.addEventListener("submit", (ev) => {
    void onSubmit(ev);
  });

  // Re-render i18n strings quando idioma muda — cobre o label do botão,
  // o status message ativo e (se houver erro vivo) as mensagens de validação.
  onLanguageChange(() => {
    if (submitLabel) submitLabel.textContent = t(submitLabelKey(state));
    if (statusEl && lastStatusKind && lastStatusKey) {
      statusEl.textContent = t(lastStatusKey);
    }
    if (lastStatusKind === "error") {
      const parsed = getSchema().safeParse(readValues());
      if (!parsed.success) showErrors(parsed.error.issues);
    }
  });
}
